/** Custom Estimates — list page. Multi-supplier job costing for remodels / mixed-vendor jobs. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";

export default function CustomEstimates() {
  const [items, setItems] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get("/custom-estimates").then((r) => setItems(r.data));
    api.get("/custom-estimates/templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const createNew = async () => {
    try {
      const r = await api.post(`/custom-estimates${tplId ? `?template_id=${tplId}` : ""}`, { title: "", markup_pct: 30 });
      navigate(`/custom-estimates/${r.data.id}`);
    } catch {
      toast.error("Could not create estimate");
    }
  };

  const deleteTemplate = async () => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl || !window.confirm(`Delete template “${tpl.template_name}”?`)) return;
    await api.delete(`/custom-estimates/${tplId}`);
    setTplId("");
    toast.success("Template deleted");
    load();
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this estimate?")) return;
    await api.delete(`/custom-estimates/${id}`);
    toast.success("Estimate deleted");
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-1">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Custom Estimates</h1>
          <p className="text-xs text-zinc-500 mt-1">Multi-supplier job costing — remodels and mixed-vendor projects. Materials from any store, labor by the hour, sub bids, variable markup.</p>
        </div>
        <div className="flex items-end gap-2">
          {templates.length > 0 && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Start from template</label>
              <div className="flex items-center gap-1">
                <select value={tplId} onChange={(e) => setTplId(e.target.value)}
                  className="h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white" data-testid="estimate-template-select">
                  <option value="">Blank estimate</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.template_name}</option>)}
                </select>
                {tplId && (
                  <button onClick={deleteTemplate} title="Delete this template" data-testid="delete-template-btn"
                    className="h-9 px-2 text-[10px] font-bold uppercase tracking-wider text-red-700 border border-zinc-300 rounded-sm hover:border-red-700">✕</button>
                )}
              </div>
            </div>
          )}
          <button onClick={createNew} data-testid="new-custom-estimate-btn"
            className="h-9 px-4 bg-blue-900 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800">
            + New Estimate
          </button>
        </div>
      </div>

      <div className="mt-5 border border-zinc-200 rounded-sm overflow-x-auto bg-white">
        <table className="w-full text-sm" data-testid="custom-estimates-table">
          <thead>
            <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="px-5 py-3">Title</th><th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Cost</th><th className="px-5 py-3 text-right">Sell</th>
              <th className="px-5 py-3 text-right">Est. Profit</th><th className="px-5 py-3">Suppliers</th><th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td className="px-5 py-6 text-zinc-400" colSpan={7}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td className="px-5 py-6 text-zinc-400" colSpan={7}>No custom estimates yet — hit “+ New Estimate”.</td></tr>
            ) : items.map((e) => (
              <tr key={e.id} onClick={() => navigate(`/custom-estimates/${e.id}`)}
                className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`estimate-row-${e.id}`}>
                <td className="px-5 py-3 font-bold text-zinc-950">{e.title || "Untitled estimate"}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${e.status === "Final" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-600 border-zinc-300"}`}>{e.status}</span>
                </td>
                <td className="px-5 py-3 font-mono text-right">{formatCurrency(e.total_cost)}</td>
                <td className="px-5 py-3 font-mono text-right font-bold">{formatCurrency(e.sell_price)}</td>
                <td className="px-5 py-3 font-mono text-right text-emerald-700 font-bold">{formatCurrency(e.est_profit)}</td>
                <td className="px-5 py-3 text-xs text-zinc-500">{Object.keys(e.by_supplier || {}).join(", ") || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={(ev) => remove(ev, e.id)} data-testid={`delete-estimate-${e.id}`}
                    className="text-[10px] font-bold uppercase tracking-wider text-red-700 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
